import type { ComponentProps, ReactNode } from 'react';
import { StyleSheet } from 'react-native-unistyles';
import Button, { type ButtonVariant } from '@/components/UI/Button';
import Box from '@/components/Unistyles/Box';
import Icon from '@/components/Unistyles/Icon';
import { DEFAULT_COUNTDOWN_MS } from '@/hooks/useCountdown';
import CountdownFill from './CountdownFill';
import Spinner, { type SpinnerProps } from './Spinner';

export default function FooterActionButton({
  label,
  icon,
  appearance = 'solid',
  loading = false,
  loadingLabel,
  countdownMs = 0,
  countdownDurationMs = DEFAULT_COUNTDOWN_MS,
  disabled = false,
  variant = 'primary',
  accessibilityLabel,
  accessibilityState,
  style: extraStyles,
  containerStyle,
  secondaryAction,
  footerLayout = 'centered',
  ...props
}: Omit<ComponentProps<typeof Button>, 'children'> & {
  secondaryAction?: ReactNode;
  footerLayout?: 'centered' | 'trailing';
  containerStyle?: ComponentProps<typeof Box>['style'];
  label: string;
  icon?: ComponentProps<typeof Icon>['name'];
  loading?: boolean;
  loadingLabel?: string;
  countdownMs?: number;
  countdownDurationMs?: number;
}) {
  style.useVariants({ footerLayout });
  const countdownSeconds = Math.ceil(countdownMs / 1000);
  const isDisabled = disabled || loading || countdownMs > 0;
  const readyLabel =
    countdownMs > 0
      ? `${accessibilityLabel ?? label}. Available in ${countdownSeconds} seconds`
      : accessibilityLabel;
  const busyLabel = loadingLabel ?? `${label}…`;

  return (
    <Box style={[style.container, containerStyle]}>
      <Box style={style.actions}>
        {secondaryAction}
        <Box
          style={style.primaryAction}
          pointerEvents={isDisabled ? 'none' : 'auto'}
        >
          <Button
            size="lg"
            {...props}
            variant={variant}
            appearance={appearance}
            disabled={isDisabled}
            accessibilityLabel={loading ? busyLabel : readyLabel}
            accessibilityState={{
              ...accessibilityState,
              busy: loading,
              disabled: isDisabled,
            }}
            style={[
              style.button,
              countdownMs > 0 && !disabled && !loading && style.countingDown,
              extraStyles,
            ]}
          >
            {!loading && countdownMs > 0 && (
              <CountdownFill durationMs={countdownDurationMs} />
            )}
            {loading && (
              <Spinner
                size="sm"
                variant={spinnerVariants[variant]}
                spinnerStyle={style.spinner}
              />
            )}
            {!loading && icon && (
              <Icon
                name={icon}
                size="xl"
                variant={
                  appearance === 'solid' ? 'white' : spinnerVariants[variant]
                }
              />
            )}
            <Button.Text
              size="xl"
              fontWeight="semibold"
              numberOfLines={0}
              flexShrink={1}
              center
            >
              {loading ? busyLabel : label}
            </Button.Text>
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

const spinnerVariants = {
  primary: 'primary',
  secondary: 'secondary',
  success: 'success',
  info: 'info',
  warning: 'warning',
  error: 'error',
  negative: 'error',
  positive: 'success',
} satisfies Record<ButtonVariant, NonNullable<SpinnerProps['variant']>>;

const style = StyleSheet.create((theme) => ({
  container: {
    alignSelf: 'stretch',
    margin: theme.space('lg'),
  },
  actions: {
    alignSelf: 'center',
    width: '100%',
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: theme.space('lg'),
    variants: {
      footerLayout: {
        centered: {},
        trailing: {
          alignSelf: { base: 'stretch', lg: 'flex-end' },
          width: { base: '100%', lg: 'auto' },
        },
      },
    },
  },
  primaryAction: {
    flex: 1,
    minWidth: 0,
    variants: {
      footerLayout: {
        centered: {},
        trailing: {
          flex: { base: 1, lg: 0 },
          flexGrow: { base: 1, lg: 0 },
          flexShrink: 1,
          flexBasis: { base: 0, lg: 'auto' },
          minWidth: { base: 0, lg: theme.size(40) },
        },
      },
    },
  },
  button: {
    overflow: 'hidden',
    alignSelf: 'center',
    width: '100%',
    alignItems: 'center',
    minHeight: theme.size(13),
    borderRadius: theme.borderRadius.full,
    borderCurve: 'continuous',
    justifyContent: 'center',
    variants: {
      footerLayout: {
        centered: {},
        trailing: {
          width: { base: '100%', lg: 'auto' },
          minWidth: { base: 0, lg: theme.size(40) },
        },
      },
    },
  },
  countingDown: {
    opacity: 1,
  },
  spinner: {
    borderTopColor: theme.colors.white,
  },
}));
